/**
 * Map input (M3 plan R14, §2.9 flow 3, §2.10): held keys and the polled pad → map actions, with edges against the
 * map's own previous state (M1's edge actions and the reach input are left alone). Pure and allocation-free: the
 * maps feature calls `readMapInput` every frame, open or closed, so nothing held across an open or a close fires.
 *
 * - Keyboard: G toggles, Enter acts (plot the route or set the cruise target), Q/E change level.
 * - Pad: View held 0.5 s toggles, A selects or acts, B goes back, LB/RB change level, the left stick moves the
 *   cursor, the right stick orbits, and the triggers zoom (LT out, RT in).
 */
import type { PadLike } from '../../input/playerInput'
import type { ReachControls } from '../../reach/controls'
import { anyKeyHeld, holdReached, holdTime, padPressed } from '../../reach/input'
import type { MapsViewConfig } from './config'

export type MapLevel = 'galaxy' | 'system'

export interface MapActions {
  /** G, or View held `gamepad.map.holdS`: open, or close. */
  toggle: boolean
  /** Enter: plot the route (galaxy level) or set the cruise target (system level). */
  plot: boolean
  /** Q or LB: out to the galaxy map. */
  zoomOut: boolean
  /** E or RB: into the selected system. */
  zoomIn: boolean
  /** Pad A: select what the cursor is over, or act on the selection. */
  select: boolean
  /** Pad B: back (cancel a prompt, galaxy → system, then close). */
  back: boolean
  /** Left stick after the deadzone, −1..1 (+x right, +y down). */
  cursorX: number
  cursorY: number
  /** Right stick after the deadzone, −1..1. */
  orbitX: number
  orbitY: number
  /** Trigger zoom, −1..1 (positive = out). */
  zoom: number
  /** Any pad stick, trigger or map button was used this frame (shows the virtual cursor). */
  padActive: boolean
}

export interface MapInputPrev {
  mapKey: boolean
  plotKey: boolean
  zoomOutKey: boolean
  zoomInKey: boolean
  selectPad: boolean
  backPad: boolean
  zoomOutPad: boolean
  zoomInPad: boolean
  /** Seconds View has been held (0 while up). */
  mapPadS: number
}

export function createMapActions(): MapActions {
  return {
    toggle: false,
    plot: false,
    zoomOut: false,
    zoomIn: false,
    select: false,
    back: false,
    cursorX: 0,
    cursorY: 0,
    orbitX: 0,
    orbitY: 0,
    zoom: 0,
    padActive: false,
  }
}

export function createMapInputPrev(): MapInputPrev {
  return { mapKey: false, plotKey: false, zoomOutKey: false, zoomInKey: false, selectPad: false, backPad: false, zoomOutPad: false, zoomInPad: false, mapPadS: 0 }
}

/** A stick axis with its deadzone removed and the rest rescaled to 0..1 (sign kept). */
export function stickAxis(v: number, deadzone: number): number {
  const a = v < 0 ? -v : v
  if (!(a > deadzone)) return 0
  const s = (Math.min(a, 1) - deadzone) / (1 - deadzone)
  return v < 0 ? -s : s
}

/** A trigger's analog amount (0..1), or 1/0 from `pressed` when the pad reports no value. */
export function triggerAmount(pad: PadLike | null, index: number): number {
  const b = pad?.buttons[index]
  if (b === undefined) return 0
  return typeof b.value === 'number' ? b.value : b.pressed ? 1 : 0
}

/** Reads `keys` and `pad` into `out` with edges against `prev` (updated in place). Allocation-free. */
export function readMapInput(
  keys: ReadonlySet<string>,
  pad: PadLike | null,
  controls: ReachControls,
  cfg: MapsViewConfig,
  dt: number,
  prev: MapInputPrev,
  out: MapActions,
): MapActions {
  const k = controls.keyboard
  const g = controls.gamepad

  const mapKey = anyKeyHeld(keys, k.map)
  const mapS = holdTime(prev.mapPadS, padPressed(pad, g.map.button), dt)
  out.toggle = (mapKey && !prev.mapKey) || holdReached(prev.mapPadS, mapS, g.map.holdS)
  prev.mapKey = mapKey
  prev.mapPadS = mapS

  const plotKey = anyKeyHeld(keys, k.plotRoute)
  out.plot = plotKey && !prev.plotKey
  prev.plotKey = plotKey

  const zoomOutKey = anyKeyHeld(keys, k.mapZoomOut)
  const zoomInKey = anyKeyHeld(keys, k.mapZoomIn)
  const selectPad = padPressed(pad, g.mapSelect)
  const backPad = padPressed(pad, g.mapBack)
  const zoomOutPad = padPressed(pad, g.mapZoomOut)
  const zoomInPad = padPressed(pad, g.mapZoomIn)
  out.zoomOut = (zoomOutKey && !prev.zoomOutKey) || (zoomOutPad && !prev.zoomOutPad)
  out.zoomIn = (zoomInKey && !prev.zoomInKey) || (zoomInPad && !prev.zoomInPad)
  out.select = selectPad && !prev.selectPad
  out.back = backPad && !prev.backPad
  prev.zoomOutKey = zoomOutKey
  prev.zoomInKey = zoomInKey
  prev.selectPad = selectPad
  prev.backPad = backPad
  prev.zoomOutPad = zoomOutPad
  prev.zoomInPad = zoomInPad

  const dz = cfg.cursor.stickDeadzone
  const p = cfg.pad
  if (pad !== null) {
    out.cursorX = stickAxis(pad.axes[p.cursorAxes[0]] ?? 0, dz)
    out.cursorY = stickAxis(pad.axes[p.cursorAxes[1]] ?? 0, dz)
    out.orbitX = stickAxis(pad.axes[p.orbitAxes[0]] ?? 0, dz)
    out.orbitY = stickAxis(pad.axes[p.orbitAxes[1]] ?? 0, dz)
    const zOut = triggerAmount(pad, p.zoomOutTrigger)
    const zIn = triggerAmount(pad, p.zoomInTrigger)
    const t = cfg.zoom.triggerThreshold
    out.zoom = (zOut > t ? zOut : 0) - (zIn > t ? zIn : 0)
  } else {
    out.cursorX = 0
    out.cursorY = 0
    out.orbitX = 0
    out.orbitY = 0
    out.zoom = 0
  }
  out.padActive =
    out.cursorX !== 0 || out.cursorY !== 0 || out.orbitX !== 0 || out.orbitY !== 0 || out.zoom !== 0 || selectPad || backPad || zoomOutPad || zoomInPad
  return out
}

/** Pad A: select the hovered item when it is not the selection; otherwise act on the selection (if any). */
export function resolveSelect(hovered: number, selected: number): 'select' | 'act' | 'none' {
  if (hovered >= 0 && hovered !== selected) return 'select'
  return selected >= 0 ? 'act' : 'none'
}

export type BackResult = 'cancelPrompt' | 'toSystem' | 'close'

/** Pad B: cancel an open prompt first, then go from the galaxy map back to the system map, then close. */
export function resolveBack(prompt: boolean, level: MapLevel): BackResult {
  if (prompt) return 'cancelPrompt'
  return level === 'galaxy' ? 'toSystem' : 'close'
}

/** Esc: cancel an open prompt, otherwise close the map (plan §2.9 flow 3: G or Esc closes). */
export function resolveEscape(prompt: boolean): 'cancelPrompt' | 'close' {
  return prompt ? 'cancelPrompt' : 'close'
}

/** Moves the virtual cursor by the stick for `dt` seconds, clamped to the viewport. Writes into `out`. */
export function moveCursor(
  x: number,
  y: number,
  sx: number,
  sy: number,
  dt: number,
  speedPxS: number,
  width: number,
  height: number,
  out: { x: number; y: number },
): { x: number; y: number } {
  out.x = Math.min(Math.max(x + sx * speedPxS * dt, 0), width)
  out.y = Math.min(Math.max(y + sy * speedPxS * dt, 0), height)
  return out
}
